import { useMemo, useState } from "react";
import Table from "../../components/Table";
import Error from "../../components/Error";
import {
  MerchantAccount,
  WebhookConfiguration,
} from "../../services/accountService";
import { AccountHolderUser } from "../../services/accountService";
import { getEmptyMerchantAccount } from "../../utils/accounts";
import { getEmptyUser } from "../../utils/users";
import { getMerchantAccountTableColumns } from "./merchantAccountsTableColumns";
import { getUserDetailsTableColumns } from "../customerSettings/userDetailsTableColumns";
import useMerchantAccountsSysConfigQuery from "../../hooks/data/queries/useMerchantAccountsSysConfigQuery";
import HasAccess, { csrRoles } from "../../components/HasAccess";
import useWebhookConfigQuery from "../../hooks/data/queries/useWebhookConfigQuery";
import { getWebhooksConfigurationsTableColumns } from "./webhooksConfigurationsTableColumns";
import Button from "../../components/Button";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import Modal from "../../components/Modal";
import SignUpForm from "../onboarding/SignUpForm";
import { useSnackbar } from "../../components/Snackbar";
import useCreateUserMutation from "../../hooks/data/mutations/useCreateUserMutation";
import { SignUpFormData } from "../../services/userService";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import Spinner from "../../components/Spinner";
import useUpdateUserStatusMutation from "../../hooks/data/mutations/useUpdateUserStatusMutation";
import { useQueryClient } from "@tanstack/react-query";
import useSystemUsersQuery from "../../hooks/data/queries/useSystemUsersQuery";

function SystemSettings(): JSX.Element {
  const companyId = "JackRabbitTech"; // TODO: Harcoded for now
  const { data: currentUserAccountData } = useCurrentUserAccount();
  const {
    data: usersData,
    isLoading: usersIsLoading,
    error: usersError,
  } = useSystemUsersQuery();
  const {
    data: merchantAccounts,
    isLoading: isLoadingMerchantAccounts,
    error: merchantAccountsError,
  } = useMerchantAccountsSysConfigQuery(companyId);
  const {
    data: webhookConfigData,
    isLoading: isLoadingWebhookConfig,
    error: webhookConfigError,
  } = useWebhookConfigQuery();

  const memoUsers = useMemo(() => {
    if (usersIsLoading && !usersData) {
      return Array.from<AccountHolderUser>({ length: 5 }).fill(getEmptyUser());
    }
    return usersData?.at(0)?.userInfo ?? [];
  }, [usersData, usersIsLoading]);

  const memoMerchantAccounts = useMemo(() => {
    if (isLoadingMerchantAccounts && !merchantAccounts) {
      return Array.from<MerchantAccount>({ length: 5 }).fill(
        getEmptyMerchantAccount()
      );
    }
    return merchantAccounts ?? [];
  }, [merchantAccounts, isLoadingMerchantAccounts]);

  const memoWebhookConfigurations = useMemo(() => {
    if (isLoadingWebhookConfig && !webhookConfigData) {
      return Array.from<WebhookConfiguration>({ length: 6 }).fill({
        edition: "",
        environment: "",
        url: "",
      });
    }
    return webhookConfigData ?? [];
  }, [webhookConfigData, isLoadingWebhookConfig]);

  const { mutate, status } = useCreateUserMutation();
  const { mutate: updateUserStatus, isLoading: isUpdatingUserStatus } =
    useUpdateUserStatusMutation();

  const queryClient = useQueryClient();

  const [showSignUpModal, setShowSignupModal] = useState(false);
  const [showUpdateUserStatusModal, setShowUpdateUserStatusModal] =
    useState(false);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentUserStatus, setCurrentUserStatus] = useState(false);

  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  async function handleCreateUserFormSubmit(signupFormData: SignUpFormData) {
    mutate(signupFormData, {
      onSuccess: ({ firstName, lastName }) => {
        openSnackbar(`User ${firstName} ${lastName} was successfully added.`, {
          type: "success",
        });
        queryClient.invalidateQueries(["systemUsers"]);
      },
      onError: (error) => {
        openSnackbar(error, { type: "error" });
      },
      onSettled: () => {
        setShowSignupModal(false);
      },
    });
  }

  function handleUpdateUserStatus() {
    updateUserStatus(
      {
        id: currentUserId,
        enabled: !currentUserStatus,
      },
      {
        onSuccess() {
          queryClient.invalidateQueries(["systemUsers"]);
          openSnackbar("User status updated.", {
            type: "success",
          });
        },
        onError(e) {
          openSnackbar(e, {
            type: "error",
          });
        },
        onSettled() {
          setShowUpdateUserStatusModal(false);
        },
      }
    );
  }

  return (
    <>
      <HasAccess
        allowedRoles={[...csrRoles]}
        notAllowedComponent={
          <Error message="You're not allowed to view this page." />
        }
      >
        <div className="flex flex-col px-6">
          <h1 className="mb-4 text-3xl">System Settings</h1>

          <h3 className="my-3 text-xl">Adyen Merchant Accounts</h3>
          {merchantAccountsError ? (
            <Error message={merchantAccountsError} />
          ) : (
            <Table
              id="merchantAccounts"
              data={memoMerchantAccounts}
              columns={getMerchantAccountTableColumns()}
              isLoading={usersIsLoading}
              showPaginator={false}
              noRowsText="There are no merchant accounts to display."
            />
          )}

          <h3 className="my-3 text-xl">Webhook Configurations</h3>
          {webhookConfigError ? (
            <Error message={webhookConfigError} />
          ) : (
            <Table
              id="webhookConfigurations"
              data={memoWebhookConfigurations}
              columns={getWebhooksConfigurationsTableColumns()}
              isLoading={isLoadingWebhookConfig}
              showPaginator={false}
              noRowsText="There are no webhook configurations to display."
            />
          )}
          <div className="flex items-center justify-between">
            <h3 className="my-3 text-xl">CSR User Management</h3>
            <HasAccess allowedRoles={["ADMIN"]}>
              <Button
                size="sm"
                icon={<PlusIcon />}
                onClick={() => setShowSignupModal(true)}
              >
                Add User
              </Button>
            </HasAccess>
          </div>
          {usersError ? (
            <Error message={usersError} />
          ) : (
            <Table
              id="userDetails"
              data={memoUsers}
              columns={getUserDetailsTableColumns()}
              isLoading={isLoadingMerchantAccounts}
              showPaginator={false}
              noRowsText="There are no users to display."
              meta={{
                role: currentUserAccountData?.role,
                onStatusSwitchToggle: (id: string, status: boolean) => {
                  setShowUpdateUserStatusModal(true);
                  setCurrentUserId(id);
                  setCurrentUserStatus(status);
                },
              }}
            />
          )}
        </div>
      </HasAccess>
      <HasAccess allowedRoles={["ADMIN"]}>
        <Modal
          isOpen={showSignUpModal}
          title="Sign Up"
          onClose={() => setShowSignupModal(false)}
        >
          <SignUpForm
            accountHolderInfo={{
              edition: "",
              jackrabbitId: "",
              companyName: "",
            }}
            invitedUserEmail=""
            createUserStatus={status}
            onSubmit={handleCreateUserFormSubmit}
            onCancel={() => setShowSignupModal(false)}
            forAdmin
          />
        </Modal>
      </HasAccess>
      <HasAccess allowedRoles={["ADMIN"]}>
        <Modal
          isOpen={showUpdateUserStatusModal}
          title={currentUserStatus ? "Disable User" : "Enable User"}
          onClose={() => setShowUpdateUserStatusModal(false)}
        >
          <div className="mt-5 flex flex-col">
            <p className="py-2">
              Change status to {currentUserStatus ? "Inactive" : "Active"}?
            </p>
            <div className="mt-4 flex justify-center gap-x-16">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowUpdateUserStatusModal(false)}
                className="w-20"
              >
                No
              </Button>
              <Button
                size="sm"
                onClick={handleUpdateUserStatus}
                className="w-20 justify-center"
                disabled={isUpdatingUserStatus}
                icon={
                  isUpdatingUserStatus && (
                    <Spinner className="mt-0.5 mr-1 text-white" />
                  )
                }
              >
                Yes
              </Button>
            </div>
          </div>
        </Modal>
      </HasAccess>
    </>
  );
}

export default SystemSettings;
